import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

type TabType = 'duplicates' | 'trash' | 'storage' | 'thumbnails' | 'tags' | 'cleanup';

interface DuplicateGroup {
  hash: string;
  count: number;
  totalSize: number;
  files: { filename: string; size: number }[];
}

interface TrashItem {
  id: string;
  filename: string;
  size: number;
  deletedAt: string;
}

interface StorageAnalysis {
  totalVideos: number;
  totalSize: number;
  totalSizeMB: string;
  sizeDistribution: { small: number; medium: number; large: number; xlarge: number };
  formatDistribution: Record<string, number>;
  uploadTrend: Record<string, { count: number; size: number }>;
}

const AdminTools: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('storage');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // 去重检测状态
  const [duplicates, setDuplicates] = useState<DuplicateGroup[]>([]);
  const [duplicateStats, setDuplicateStats] = useState({ totalDuplicates: 0, totalWastedSpace: 0 });

  // 回收站状态
  const [trashItems, setTrashItems] = useState<TrashItem[]>([]);

  // 存储分析状态
  const [storageAnalysis, setStorageAnalysis] = useState<StorageAnalysis | null>(null);

  // 缩略图状态
  const [thumbnailProgress, setThumbnailProgress] = useState<string | null>(null);

  // 标签推荐状态
  const [tagText, setTagText] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);

  // 视频清理状态
  const [cleanupOptions, setCleanupOptions] = useState({
    autoTranscode: true,
    deleteInvalid: true
  });
  const [isCleaning, setIsCleaning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<any>(null);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [taskProgress, setTaskProgress] = useState<{ progress: number; current: string; status: string } | null>(null);

  const showMessage = useCallback((msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(null), 3000);
  }, []);

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 轮询任务状态
  const pollTaskStatus = async (taskId: string) => {
    let attempts = 0;
    const maxAttempts = 3600; // 最多轮询1小时（每秒一次）
    
    const interval = setInterval(async () => {
      try {
        attempts++;
        const res = await fetch(`/api/admin/cleanup-tasks/${taskId}`);
        
        if (!res.ok) {
          clearInterval(interval);
          setIsCleaning(false);
          setTaskProgress(null);
          showMessage('❌ 任务查询失败');
          return;
        }
        
        const data = await res.json();
        const task = data.task;
        
        // 更新进度
        setTaskProgress({
          progress: task.progress,
          current: task.current || '',
          status: task.status
        });
        
        // 任务完成或失败
        if (task.status === 'completed') {
          clearInterval(interval);
          setIsCleaning(false);
          setCurrentTaskId(null);
          setCleanupResult({
            summary: {
              total: task.total,
              validCount: task.results.valid.length,
              transcodedCount: task.results.transcoded.length,
              deletedCount: task.results.deleted.length,
              failedCount: task.results.failed.length
            },
            details: task.results
          });
          showMessage(`✅ 清理完成！耗时 ${task.elapsed} 秒`);
        } else if (task.status === 'failed') {
          clearInterval(interval);
          setIsCleaning(false);
          setCurrentTaskId(null);
          showMessage(`❌ 任务失败: ${task.error || '未知错误'}`);
        }
        
        // 超时保护
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          setIsCleaning(false);
          setTaskProgress(null);
          showMessage('⚠️ 任务超时');
        }
        
      } catch (error) {
        console.error('轮询失败:', error);
      }
    }, 1000); // 每秒轮询一次
  };

  // ========== 去重检测 ==========
  const fetchDuplicates = async () => {
    setIsLoading(true);
    try {
      const res = await fetch('/api/admin/duplicates');
      const data = await res.json();
      setDuplicates(data.duplicateGroups || []);
      setDuplicateStats({
        totalDuplicates: data.totalDuplicates || 0,
        totalWastedSpace: data.totalWastedSpace || 0
      });
    } catch (e) {
      showMessage('检测失败');
    } finally {
      setIsLoading(false);
    }
  };

  const removeDuplicates = async (hash: string) => {
    if (!window.confirm('确定删除重复视频（保留第一个）吗？')) return;
    try {
      const res = await fetch('/api/admin/duplicates/remove', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hash })
      });
      const data = await res.json();
      showMessage(data.message);
      fetchDuplicates();
    } catch (e) {
      showMessage('删除失败');
    }
  };

  // ========== 回收站 ==========
  const fetchTrash = async () => {
    try {
      const res = await fetch('/api/trash');
      const data = await res.json();
      setTrashItems(data);
    } catch (e) {
      showMessage('获取回收站失败');
    }
  };

  const restoreVideo = async (id: string) => {
    try {
      const res = await fetch(`/api/trash/${id}/restore`, { method: 'POST' });
      if (res.ok) {
        showMessage('视频已恢复');
        fetchTrash();
      }
    } catch (e) {
      showMessage('恢复失败');
    }
  };

  const permanentDelete = async (id: string) => {
    if (!window.confirm('确定永久删除吗？此操作不可恢复！')) return;
    try {
      const res = await fetch(`/api/trash/${id}`, { method: 'DELETE' });
      if (res.ok) {
        showMessage('已永久删除');
        fetchTrash();
      }
    } catch (e) {
      showMessage('删除失败');
    }
  };

  const emptyTrash = async () => {
    if (!window.confirm('确定清空回收站吗？所有视频将被永久删除！')) return;
    try {
      const res = await fetch('/api/trash', { method: 'DELETE' });
      if (res.ok) {
        showMessage('回收站已清空');
        fetchTrash();
      }
    } catch (e) {
      showMessage('清空失败');
    }
  };

  // ========== 存储分析 ==========
  const fetchStorageAnalysis = async () => {
    setIsLoading(true);
    try {
      const res = await fetch('/api/admin/storage-analysis');
      const data = await res.json();
      setStorageAnalysis(data);
    } catch (e) {
      showMessage('获取存储分析失败');
    } finally {
      setIsLoading(false);
    }
  };

  // ========== 缩略图 ==========
  const generateThumbnails = async () => {
    try {
      setThumbnailProgress('批量生成任务已启动，请稍后刷新查看');
      const res = await fetch('/api/thumbnails/batch-generate', { method: 'POST' });
      if (res.ok) {
        showMessage('缩略图生成任务已启动');
      }
    } catch (e) {
      showMessage('启动失败');
    }
  };

  // ========== 标签推荐 ==========
  const fetchTagSuggestions = async () => {
    if (!tagText.trim()) return;
    try {
      const res = await fetch(`/api/tags/suggest?text=${encodeURIComponent(tagText)}`);
      const data = await res.json();
      setTagSuggestions(data.suggestions || []);
    } catch (e) {
      showMessage('获取推荐失败');
    }
  };

  // 切换标签页时加载数据
  useEffect(() => {
    if (activeTab === 'duplicates') fetchDuplicates();
    else if (activeTab === 'trash') fetchTrash();
    else if (activeTab === 'storage') fetchStorageAnalysis();
  }, [activeTab]);

  const tabs = [
    { id: 'storage' as TabType, label: '📊 存储分析', icon: '📊' },
    { id: 'cleanup' as TabType, label: '🧹 视频清理', icon: '🧹' },
    { id: 'duplicates' as TabType, label: '🔍 去重检测', icon: '🔍' },
    { id: 'trash' as TabType, label: '🗑️ 回收站', icon: '🗑️' },
    { id: 'thumbnails' as TabType, label: '🖼️ 缩略图', icon: '🖼️' },
    { id: 'tags' as TabType, label: '🏷️ 标签推荐', icon: '🏷️' },
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* 头部 */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">🔧 管理工具箱</h1>
              <p className="text-gray-400 text-sm mt-1">视频去重、回收站、存储分析、缩略图管理</p>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded-lg transition-colors"
            >
              ← 返回后台
            </button>
          </div>
        </div>
      </div>

      {/* 消息提示 */}
      {message && (
        <div className="max-w-7xl mx-auto px-4 mt-4">
          <div className="bg-blue-900/50 border border-blue-500 rounded-lg p-3 flex justify-between items-center">
            <span>{message}</span>
            <button onClick={() => setMessage(null)} className="text-blue-300 hover:text-white">✕</button>
          </div>
        </div>
      )}

      {/* 标签页 */}
      <div className="max-w-7xl mx-auto px-4 mt-6">
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* 加载中 */}
        {isLoading && (
          <div className="text-center py-12">
            <div className="w-10 h-10 border-3 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-400">处理中...</p>
          </div>
        )}

        {/* ========== 存储分析 ========== */}
        {activeTab === 'storage' && storageAnalysis && (
          <div className="space-y-6">
            {/* 概览卡片 */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gray-800 p-5 rounded-lg">
                <div className="text-3xl font-bold text-blue-400">{storageAnalysis.totalVideos}</div>
                <div className="text-gray-400 text-sm mt-1">视频总数</div>
              </div>
              <div className="bg-gray-800 p-5 rounded-lg">
                <div className="text-3xl font-bold text-green-400">{storageAnalysis.totalSizeMB}</div>
                <div className="text-gray-400 text-sm mt-1">总大小 (MB)</div>
              </div>
              <div className="bg-gray-800 p-5 rounded-lg">
                <div className="text-3xl font-bold text-yellow-400">
                  {Object.keys(storageAnalysis.formatDistribution).length}
                </div>
                <div className="text-gray-400 text-sm mt-1">格式种类</div>
              </div>
              <div className="bg-gray-800 p-5 rounded-lg">
                <div className="text-3xl font-bold text-purple-400">
                  {Object.keys(storageAnalysis.uploadTrend).length}
                </div>
                <div className="text-gray-400 text-sm mt-1">活跃月份</div>
              </div>
            </div>

            {/* 大小分布 */}
            <div className="bg-gray-800 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">📦 视频大小分布</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: '< 10MB', count: storageAnalysis.sizeDistribution.small, color: 'bg-green-500' },
                  { label: '10-50MB', count: storageAnalysis.sizeDistribution.medium, color: 'bg-blue-500' },
                  { label: '50-100MB', count: storageAnalysis.sizeDistribution.large, color: 'bg-yellow-500' },
                  { label: '> 100MB', count: storageAnalysis.sizeDistribution.xlarge, color: 'bg-red-500' },
                ].map(item => (
                  <div key={item.label} className="bg-gray-700/50 p-4 rounded-lg">
                    <div className={`w-3 h-3 rounded-full ${item.color} mb-2`} />
                    <div className="text-2xl font-bold">{item.count}</div>
                    <div className="text-gray-400 text-sm">{item.label}</div>
                  </div>
                ))}
              </div>
              {/* 可视化条形图 */}
              <div className="mt-6 space-y-3">
                {[
                  { label: '< 10MB', count: storageAnalysis.sizeDistribution.small, color: 'bg-green-500' },
                  { label: '10-50MB', count: storageAnalysis.sizeDistribution.medium, color: 'bg-blue-500' },
                  { label: '50-100MB', count: storageAnalysis.sizeDistribution.large, color: 'bg-yellow-500' },
                  { label: '> 100MB', count: storageAnalysis.sizeDistribution.xlarge, color: 'bg-red-500' },
                ].map(item => {
                  const max = Math.max(
                    storageAnalysis.sizeDistribution.small,
                    storageAnalysis.sizeDistribution.medium,
                    storageAnalysis.sizeDistribution.large,
                    storageAnalysis.sizeDistribution.xlarge
                  ) || 1;
                  const pct = (item.count / max) * 100;
                  return (
                    <div key={item.label} className="flex items-center gap-3">
                      <span className="w-20 text-sm text-gray-400 text-right">{item.label}</span>
                      <div className="flex-1 bg-gray-700 rounded-full h-6 overflow-hidden">
                        <div
                          className={`h-full ${item.color} transition-all duration-500 flex items-center justify-end px-2`}
                          style={{ width: `${Math.max(pct, 5)}%` }}
                        >
                          <span className="text-xs font-medium">{item.count}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 格式分布 */}
            <div className="bg-gray-800 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">🎬 格式分布</h3>
              <div className="flex flex-wrap gap-3">
                {Object.entries(storageAnalysis.formatDistribution).map(([format, count]) => (
                  <div key={format} className="bg-gray-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <span className="font-medium uppercase">{format}</span>
                    <span className="text-blue-400 font-bold">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 上传趋势 */}
            <div className="bg-gray-800 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">📈 上传趋势</h3>
              <div className="space-y-3">
                {Object.entries(storageAnalysis.uploadTrend)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([month, data]) => (
                    <div key={month} className="flex items-center justify-between bg-gray-700/30 p-3 rounded-lg">
                      <span className="font-medium">{month}</span>
                      <div className="flex gap-6">
                        <span className="text-blue-400">{data.count} 个视频</span>
                        <span className="text-green-400">{formatSize(data.size)}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* ========== 去重检测 ========== */}
        {activeTab === 'duplicates' && (
          <div className="space-y-6">
            <div className="bg-gray-800 p-6 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold">🔍 重复视频检测</h3>
                  {duplicateStats.totalDuplicates > 0 && (
                    <p className="text-gray-400 text-sm mt-1">
                      发现 {duplicateStats.totalDuplicates} 个重复视频，
                      可释放 {formatSize(duplicateStats.totalWastedSpace)} 空间
                    </p>
                  )}
                </div>
                <button
                  onClick={fetchDuplicates}
                  className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg transition-colors"
                >
                  🔄 重新检测
                </button>
              </div>

              {duplicates.length === 0 && !isLoading && (
                <div className="text-center py-12 text-gray-400">
                  <div className="text-4xl mb-3">✅</div>
                  <p>未发现重复视频</p>
                </div>
              )}

              <div className="space-y-4">
                {duplicates.map((group, idx) => (
                  <div key={group.hash} className="bg-gray-700/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="bg-red-600 text-white text-xs px-2 py-1 rounded">
                          重复 {group.count} 个
                        </span>
                        <span className="text-gray-400 text-sm">哈希: {group.hash}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-yellow-400 text-sm">
                          可释放: {formatSize(group.totalSize * (group.count - 1) / group.count)}
                        </span>
                        <button
                          onClick={() => removeDuplicates(group.hash)}
                          className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-sm transition-colors"
                        >
                          删除重复（保留1个）
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {group.files.map((file, fidx) => (
                        <div key={file.filename} className="flex items-center justify-between bg-gray-800/50 p-2 rounded">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">#{fidx + 1}</span>
                            <span>{file.filename}</span>
                          </div>
                          <span className="text-gray-400 text-sm">{formatSize(file.size)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ========== 回收站 ========== */}
        {activeTab === 'trash' && (
          <div className="space-y-6">
            <div className="bg-gray-800 p-6 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold">🗑️ 回收站</h3>
                  <p className="text-gray-400 text-sm mt-1">
                    共 {trashItems.length} 个视频，
                    总大小: {formatSize(trashItems.reduce((s, i) => s + i.size, 0))}
                  </p>
                </div>
                {trashItems.length > 0 && (
                  <button
                    onClick={emptyTrash}
                    className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg transition-colors"
                  >
                    🗑️ 清空回收站
                  </button>
                )}
              </div>

              {trashItems.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <div className="text-4xl mb-3">🗑️</div>
                  <p>回收站是空的</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {trashItems.map(item => (
                    <div key={item.id} className="flex items-center justify-between bg-gray-700/50 p-4 rounded-lg">
                      <div className="flex items-center gap-4">
                        <span className="text-2xl">🎥</span>
                        <div>
                          <div className="font-medium">{item.filename}</div>
                          <div className="text-sm text-gray-400">
                            {formatSize(item.size)} · 删除于 {new Date(item.deletedAt).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => restoreVideo(item.id)}
                          className="bg-green-600 hover:bg-green-700 px-3 py-1 rounded text-sm transition-colors"
                        >
                          ↩️ 恢复
                        </button>
                        <button
                          onClick={() => permanentDelete(item.id)}
                          className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-sm transition-colors"
                        >
                          🗑️ 永久删除
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========== 缩略图 ========== */}
        {activeTab === 'thumbnails' && (
          <div className="space-y-6">
            <div className="bg-gray-800 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">🖼️ 缩略图管理</h3>
              <p className="text-gray-400 mb-6">
                为所有没有缩略图的视频生成封面图片。缩略图使用视频第3秒的画面。
              </p>
              <button
                onClick={generateThumbnails}
                className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg font-medium transition-colors"
              >
                🚀 批量生成缩略图
              </button>
              {thumbnailProgress && (
                <p className="mt-4 text-blue-400">{thumbnailProgress}</p>
              )}
            </div>

            <div className="bg-gray-800 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">📖 说明</h3>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li>• 缩略图使用 ffmpeg 从视频第 3 秒截取</li>
                <li>• 分辨率 480px 宽，保持原始宽高比</li>
                <li>• 缩略图文件保存在 thumbnails/ 目录</li>
                <li>• 已有缩略图的视频会跳过，不会重复生成</li>
                <li>• 缩略图生成是后台任务，刷新页面查看进度</li>
              </ul>
            </div>
          </div>
        )}

        {/* ========== 标签推荐 ========== */}
        {activeTab === 'tags' && (
          <div className="space-y-6">
            <div className="bg-gray-800 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">🏷️ 智能标签推荐</h3>
              <p className="text-gray-400 mb-4">
                输入视频标题或描述，系统会根据关键词自动推荐标签。
              </p>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={tagText}
                  onChange={(e) => setTagText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchTagSuggestions()}
                  placeholder="输入视频标题或描述..."
                  className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
                <button
                  onClick={fetchTagSuggestions}
                  className="bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded-lg transition-colors"
                >
                  🔍 推荐标签
                </button>
              </div>

              {tagSuggestions.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm text-gray-400 mb-3">推荐标签：</h4>
                  <div className="flex flex-wrap gap-2">
                    {tagSuggestions.map((tag, idx) => (
                      <span
                        key={idx}
                        className="bg-blue-600/30 text-blue-300 border border-blue-500/50 px-3 py-1 rounded-full text-sm"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-gray-800 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">📖 标签关键词库</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                {[
                  ['人物', ['美女', '帅哥', '萌娃', '老人']],
                  ['场景', ['风景', '城市', '海边', '美食']],
                  ['活动', ['舞蹈', '唱歌', '搞笑', '运动']],
                  ['其他', ['教程', '开箱', 'vlog', '萌宠']]
                ].map(([category, tags]) => (
                  <div key={category as string} className="bg-gray-700/30 p-3 rounded-lg">
                    <div className="font-medium text-gray-300 mb-2">{category}</div>
                    <div className="flex flex-wrap gap-1">
                      {(tags as string[]).map(tag => (
                        <span key={tag} className="text-xs bg-gray-600/50 px-2 py-0.5 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ========== 视频清理 ========== */}
        {activeTab === 'cleanup' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-red-900/50 to-orange-900/50 p-6 rounded-lg border border-red-700/50">
              <h3 className="text-xl font-bold mb-2">🧹 一键视频清理工具</h3>
              <p className="text-gray-300 text-sm">
                自动检测所有视频文件的完整性和编码格式，支持自动转码和删除无效文件。
              </p>
            </div>

            {/* 清理选项 */}
            <div className="bg-gray-800 p-6 rounded-lg space-y-4">
              <h4 className="text-lg font-semibold">⚙️ 清理选项</h4>
              
              <div className="flex items-center justify-between py-3 border-b border-gray-700">
                <div>
                  <div className="font-medium">自动转码不兼容格式</div>
                  <div className="text-sm text-gray-400 mt-1">
                    H.265/HEVC、VP9、MKV、AVI → H.264 + MP4
                  </div>
                </div>
                <button
                  onClick={() => setCleanupOptions(prev => ({ ...prev, autoTranscode: !prev.autoTranscode }))}
                  className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                    cleanupOptions.autoTranscode ? 'bg-green-500' : 'bg-gray-600'
                  }`}
                >
                  <div
                    className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ${
                      cleanupOptions.autoTranscode ? 'translate-x-6' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between py-3">
                <div>
                  <div className="font-medium">删除损坏的视频文件</div>
                  <div className="text-sm text-gray-400 mt-1">
                    同时清理对应的元数据和缩略图
                  </div>
                </div>
                <button
                  onClick={() => setCleanupOptions(prev => ({ ...prev, deleteInvalid: !prev.deleteInvalid }))}
                  className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                    cleanupOptions.deleteInvalid ? 'bg-red-500' : 'bg-gray-600'
                  }`}
                >
                  <div
                    className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ${
                      cleanupOptions.deleteInvalid ? 'translate-x-6' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>

              <button
                onClick={async () => {
                  if (!confirm('⚠️ 此操作将扫描所有视频并可能删除/修改文件，确定继续吗？')) {
                    return;
                  }
                  
                  setIsCleaning(true);
                  setCleanupResult(null);
                  setCurrentTaskId(null);
                  setTaskProgress(null);
                  
                  try {
                    const res = await fetch('/api/admin/cleanup-videos', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(cleanupOptions)
                    });
                    
                    if (res.ok) {
                      const data = await res.json();
                      setCurrentTaskId(data.taskId);
                      showMessage(`✅ 清理任务已启动: ${data.taskId}`);
                      
                      // 开始轮询任务状态
                      pollTaskStatus(data.taskId);
                    } else {
                      showMessage('❌ 清理失败');
                      setIsCleaning(false);
                    }
                  } catch (error) {
                    console.error('清理失败:', error);
                    showMessage('❌ 网络错误');
                    setIsCleaning(false);
                  }
                }}
                disabled={isCleaning}
                className="w-full py-3 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 rounded-lg font-bold text-white transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isCleaning ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    {taskProgress ? `处理中... ${taskProgress.progress}%` : '正在启动任务...'}
                  </>
                ) : (
                  '🚀 开始一键清理'
                )}
              </button>
            </div>

            {/* 清理进度 */}
            {taskProgress && (
              <div className="bg-gray-800 p-6 rounded-lg space-y-4">
                <h4 className="text-lg font-semibold">⏳ 清理进度</h4>
                
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-gray-400">{taskProgress.current || '准备中...'}</span>
                    <span className="text-sm font-bold text-blue-400">{taskProgress.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-600 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-purple-500 h-full transition-all duration-300"
                      style={{ width: `${taskProgress.progress}%` }}
                    />
                  </div>
                  <div className="mt-2 text-xs text-gray-500">状态: {taskProgress.status}</div>
                </div>
              </div>
            )}

            {/* 清理结果 */}
            {cleanupResult && (
              <div className="bg-gray-800 p-6 rounded-lg space-y-4">
                <h4 className="text-lg font-semibold">📊 清理结果</h4>
                
                {/* 统计卡片 */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-900/30 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-blue-400">{cleanupResult.summary.total}</div>
                    <div className="text-sm text-gray-400 mt-1">总计</div>
                  </div>
                  <div className="bg-green-900/30 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-green-400">{cleanupResult.summary.validCount}</div>
                    <div className="text-sm text-gray-400 mt-1">有效</div>
                  </div>
                  <div className="bg-yellow-900/30 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-yellow-400">{cleanupResult.summary.transcodedCount}</div>
                    <div className="text-sm text-gray-400 mt-1">已转码</div>
                  </div>
                  <div className="bg-red-900/30 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-red-400">{cleanupResult.summary.deletedCount}</div>
                    <div className="text-sm text-gray-400 mt-1">已删除</div>
                  </div>
                </div>

                {/* 详细列表 */}
                {cleanupResult.details.transcoded.length > 0 && (
                  <div>
                    <h5 className="font-medium mb-2 text-yellow-400">🔄 已转码文件</h5>
                    <div className="bg-gray-700/30 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                      {cleanupResult.details.transcoded.map((item: any, idx: number) => (
                        <div key={idx} className="text-sm flex items-center gap-2">
                          <span className="text-yellow-400">✓</span>
                          <span className="flex-1 truncate">{item.filename}</span>
                          <span className="text-xs text-gray-400">{item.fromCodec} → {item.toCodec}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {cleanupResult.details.deleted.length > 0 && (
                  <div>
                    <h5 className="font-medium mb-2 text-red-400">🗑️ 已删除文件</h5>
                    <div className="bg-gray-700/30 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                      {cleanupResult.details.deleted.map((item: any, idx: number) => (
                        <div key={idx} className="text-sm flex items-center gap-2">
                          <span className="text-red-400">✕</span>
                          <span className="flex-1 truncate">{item.filename}</span>
                          <span className="text-xs text-gray-400">{item.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {cleanupResult.details.failed.length > 0 && (
                  <div>
                    <h5 className="font-medium mb-2 text-orange-400">⚠️ 处理失败</h5>
                    <div className="bg-gray-700/30 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                      {cleanupResult.details.failed.map((item: any, idx: number) => (
                        <div key={idx} className="text-sm flex items-center gap-2">
                          <span className="text-orange-400">!</span>
                          <span className="flex-1 truncate">{item.filename}</span>
                          <span className="text-xs text-gray-400">{item.error}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTools;
